/**
 * Quem mora em my360.site e quem mora em app.my360.site.
 *
 * O site é para quem ainda está decidindo: landing, preços, soluções,
 * termos, e os tours públicos que os clientes compartilham. O app é o
 * produto: entrar, painel, editor, faturamento, administração.
 *
 * A REGRA QUE MANDA NO DESENHO é o localStorage ser por ORIGEM. O token de
 * sessão fica onde o login aconteceu. Se alguém entrasse em my360.site e
 * fosse redirecionado para app.my360.site depois, o token ficaria para
 * trás e o app pediria login de novo — em looping, porque logar de novo
 * gravaria o token no lugar errado outra vez.
 *
 * Por isso /login e /register contam como rotas do APP: a sessão precisa
 * nascer já no host onde vai ser usada.
 *
 * Uma função só decide as duas direções. Duas listas separadas — uma de
 * "manda pro app", outra de "manda pro site" — poderiam discordar sobre um
 * caminho, e o resultado seria um redirecionamento eterno entre os dois.
 */
import { isCapacitor } from '@/lib/api-base';

const SITE = 'https://my360.site';
const APP = 'https://app.my360.site';

/** Hosts nossos onde a divisão vale. Domínio de cliente nunca entra. */
const SPLIT_HOSTS = new Set(['my360.site', 'www.my360.site', 'app.my360.site']);

/**
 * Caminhos que pertencem ao app.
 *
 * Prefixos, não caminhos exatos: /editor/abc123 e /analytics/xyz precisam
 * cair aqui junto com suas raízes.
 */
const APP_PREFIXES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/google/callback',
  '/dashboard',
  '/editor',
  '/analytics',
  '/settings',
  '/billing',
  '/leads',
  '/help',
  '/sponsor',
  '/admin',
  '/vr-lab',
  '/lab',
];

export function isAppRoute(pathname: string): boolean {
  return APP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Manda o navegador para o host certo, se ele estiver no errado.
 *
 * Roda ANTES do React montar: redirecionar depois faria a pessoa ver por um
 * instante a tela que não é dela — a landing piscando antes do painel, ou o
 * contrário.
 *
 * Não age em três situações, e cada uma quebraria algo:
 *   - app nativo (origem file://), onde não existe host para trocar
 *   - desenvolvimento, onde só existe localhost
 *   - domínio white-label de cliente, que serve o mesmo SPA e não é nosso
 */
export function enforceHostSplit(): void {
  if (typeof window === 'undefined') return;
  if (import.meta.env.DEV) return;
  // isCapacitor() e nao `window.Capacitor`.
  //
  // O pacote @capacitor/status-bar, que o app importa, instala o objeto
  // global TAMBEM no navegador comum — ele existe em toda aba. Testar so a
  // existencia fazia todo navegador ser tratado como app nativo, e a
  // funcao saia na primeira linha sem nunca redirecionar ninguem.
  //
  // O que distingue de verdade e isNativePlatform(), que e o que
  // isCapacitor() consulta. O helper ja existia; eu escrevi um atalho.
  if (isCapacitor()) return;

  const { hostname, pathname, search, hash } = window.location;
  if (!SPLIT_HOSTS.has(hostname)) return;

  const onApp = hostname === 'app.my360.site';

  /**
   * Sessão órfã no site: apaga.
   *
   * Quem estava logado em my360.site antes da divisão ficou com token e
   * estado guardados NESTA origem. Como localStorage não atravessa host,
   * esses dados não servem mais para nada — todas as telas autenticadas
   * mudaram para app.my360.site.
   *
   * O estrago não é ficar deslogado, é o contrário: a interface do site
   * continua dizendo "você está logado", as chamadas voltam Unauthorized
   * porque o token expirou sem ninguém renovar, e não existe botão de sair
   * aqui para clicar — todas as telas que o teriam agora vivem no app. A
   * pessoa fica presa numa sessão fantasma.
   *
   * Apagar custa um login. Manter custa a confiança de quem não entende
   * por que o produto diz uma coisa e faz outra.
   */
  if (!onApp) {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('auth-storage');
    } catch {
      // Modo privado com armazenamento bloqueado: não há sessão para
      // limpar, e falhar aqui não pode impedir o redirecionamento.
    }
  }
  const wantsApp = isAppRoute(pathname);
  if (onApp === wantsApp) return;

  const target = `${wantsApp ? APP : SITE}${pathname}${search}${hash}`;
  // replace e não assign: o host errado não deve ficar no histórico, senão
  // o botão "voltar" devolve a pessoa para o redirecionamento.
  window.location.replace(target);
}

/** O endereço de entrada, para os botões do site apontarem direto. */
export function appUrl(path = '/login'): string {
  if (typeof window === 'undefined') return `${APP}${path}`;
  if (import.meta.env.DEV || isCapacitor()) return path;
  if (!SPLIT_HOSTS.has(window.location.hostname)) return path;
  return `${APP}${path}`;
}
